/**
 * The word filter for what people type where everyone can see it: chat messages, chat names,
 * stream titles, channel names, bios and @handles. It catches slurs, explicit sexual words and the
 * worst threats, including the usual disguises (l33t letters, s p a c e d or d.o.t.t.e.d letters,
 * repeated letters, accents and lookalike letters). Ordinary swearing is allowed. The API enforces
 * it; the website and the app check as you type. Live video is checked separately (apps/api/src/
 * lib/moderation.ts), and reports reach staff either way.
 *
 * The list is kept short on purpose: every entry must be close to meaningless outside abuse, so the
 * filter doesn't refuse ordinary words ("Scunthorpe", "snigger", "spices", "cocktail" all pass).
 */

export type BlockedTextReason = 'hate' | 'sexual' | 'harassment';

interface Entry {
  reason: BlockedTextReason;
  /** Matched as whole words, with an optional plural s. Spaces inside a phrase are optional. */
  words?: string[];
  /** Matched inside a word too ("bignigga420"), for strings no ordinary word contains. */
  inside?: string[];
  /** Letters that make an `inside` match an ordinary word when they come just before it ("snigger"). */
  notAfter?: string;
}

const ENTRIES: Entry[] = [
  { reason: 'hate', inside: ['nigger', 'nigga', 'niggah'], notAfter: 's' },
  { reason: 'hate', inside: ['faggot'] },
  {
    reason: 'hate',
    words: ['fag', 'dyke', 'tranny', 'trannies', 'kike', 'chink', 'gook', 'spic', 'wetback', 'beaner', 'coon', 'raghead', 'towelhead', 'paki', 'retard', 'retarded', 'niglet'],
  },
  { reason: 'sexual', inside: ['porn', 'onlyfans', 'blowjob', 'handjob', 'rimjob', 'cumshot', 'creampie', 'gangbang', 'bukkake', 'deepthroat', 'dildo', 'masturbat', 'jailbait', 'lolicon', 'shotacon', 'pedophil', 'paedophil', 'hentai'] },
  { reason: 'sexual', words: ['cunt', 'pussy', 'pussies', 'cock', 'cum', 'jizz', 'titties', 'whore', 'slut', 'nudes', 'loli', 'shota', 'pedo', 'paedo', 'dick pic', 'jerk off', 'jerking off'] },
  { reason: 'harassment', words: ['kys', 'kill yourself', 'kill urself', 'kill your self', 'rape', 'raped', 'raping', 'rapist'] },
];

// Lookalike letters from other alphabets, and digits and symbols used as letters.
const LOOKALIKE: Record<string, string> = {
  а: 'a', в: 'b', е: 'e', ё: 'e', к: 'k', м: 'm', н: 'h', о: 'o', р: 'p', с: 'c', т: 't', у: 'y', х: 'x', і: 'i', ї: 'i', ј: 'j', ѕ: 's', ԁ: 'd', ɡ: 'g',
  α: 'a', β: 'b', ε: 'e', ι: 'i', κ: 'k', ν: 'v', ο: 'o', ρ: 'p', τ: 't', υ: 'u', χ: 'x',
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g', '@': 'a', $: 's', '!': 'i', '|': 'i', '€': 'e', '¡': 'i',
};

/** Each letter may repeat ("niiice"); i and l stand in for each other (1 is read as i). */
function letters(word: string): string {
  return [...word].map((ch) => (ch === ' ' ? ' ?' : `${ch === 'i' || ch === 'l' ? '[il]' : ch}+`)).join('');
}

interface Compiled {
  reason: BlockedTextReason;
  whole: RegExp | null;
  inside: RegExp | null;
  notAfter: string;
}

const COMPILED: Compiled[] = ENTRIES.map((e) => ({
  reason: e.reason,
  whole: e.words?.length ? new RegExp(`(?:^| )(?:${e.words.map(letters).join('|')})s?(?= |$)`) : null,
  inside: e.inside?.length ? new RegExp(e.inside.map(letters).join('|'), 'g') : null,
  notAfter: e.notAfter ?? '',
}));

/**
 * The text as the filter reads it: lowercase a–z words separated by single spaces, with accents,
 * lookalikes and l33t letters undone, and runs of single letters ("n i g g a", "n.i.g.g.a") joined.
 */
export function filterForm(text: string): string {
  const plain = text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z ]/g, (ch) => LOOKALIKE[ch] ?? ' ');
  const words = plain.split(/ +/).filter(Boolean);
  const out: string[] = [];
  let run = '';
  for (const w of words) {
    if (w.length === 1) {
      run += w;
      continue;
    }
    if (run) out.push(run);
    run = '';
    out.push(w);
  }
  if (run) out.push(run);
  return out.join(' ');
}

/** Why a piece of text is refused, or null when it's fine. */
export function blockedTextReason(text: string): BlockedTextReason | null {
  const form = filterForm(text);
  if (!form) return null;
  for (const c of COMPILED) {
    if (c.whole?.test(form)) return c.reason;
    if (c.inside) {
      c.inside.lastIndex = 0;
      for (let m = c.inside.exec(form); m; m = c.inside.exec(form)) {
        if (!c.notAfter || !c.notAfter.includes(form[m.index - 1] ?? ' ')) return c.reason;
      }
    }
  }
  return null;
}

/** What to tell the person. `what` is the thing they typed: "That message", "That name"… */
export function blockedTextMessage(reason: BlockedTextReason, what = 'That'): string {
  if (reason === 'hate') return `${what} has a slur in it. ZIP2LIVE has zero tolerance for hate.`;
  if (reason === 'sexual') return `${what} has sexual content in it, which isn’t allowed on ZIP2LIVE.`;
  return `${what} reads as a threat or harassment, which isn’t allowed on ZIP2LIVE.`;
}
